import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { CreatePostCard } from "@/components/admin/CreatePostCard";
import { CreateScheduleCard, type ScheduleRow } from "@/components/admin/CreateScheduleCard";
import { SchedulesList } from "@/components/admin/SchedulesList";
import { QueueList, type QueueEdit, type QueueRow } from "@/components/admin/QueueList";

type Post = {
  id: string;
  slug: string;
  title: string;
  category: string;
  status: "draft" | "published";
  updated_at: string;
  published_at: string | null;
};

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <h2 className="font-display text-2xl font-bold">{title}</h2>
      {subtitle && <p className="mb-4 text-sm text-muted-foreground">{subtitle}</p>}
      <div className={subtitle ? "" : "mt-4"}>{children}</div>
    </section>
  );
}

export default function AdminBlog() {
  const navigate = useNavigate();
  const { user, isAdmin, loading } = useAdmin();
  const [posts, setPosts] = useState<Post[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [editing, setEditing] = useState<ScheduleRow | null>(null);
  const [running, setRunning] = useState(false);
  const scheduleFormRef = useRef<HTMLDivElement>(null);

  const startEdit = (s: ScheduleRow) => {
    setEditing(s);
    requestAnimationFrame(() => {
      scheduleFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login", { replace: true });
  }, [loading, user, navigate]);

  const load = useCallback(async () => {
    const [p, s, q] = await Promise.all([
      supabase.from("blog_posts").select("id,slug,title,category,status,updated_at,published_at").order("updated_at", { ascending: false }),
      supabase.from("blog_schedules").select("*").order("created_at", { ascending: false }),
      supabase.from("blog_scheduled_posts").select("*").order("scheduled_at", { ascending: true }).limit(100),
    ]);
    if (p.error) toast.error(p.error.message); else setPosts((p.data ?? []) as Post[]);
    if (s.error) toast.error(s.error.message); else setSchedules((s.data ?? []) as unknown as ScheduleRow[]);
    if (q.error) toast.error(q.error.message); else setQueue((q.data ?? []) as unknown as QueueRow[]);
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  // Light refresh so the queue reflects the server-side worker while the tab is open.
  useEffect(() => {
    if (!isAdmin) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin, load]);

  const runNow = async () => {
    setRunning(true);
    const { data, error } = await supabase.functions.invoke("run-blog-scheduler", { body: {} });
    setRunning(false);
    if (error) toast.error(error.message);
    else toast.success(data?.processed ? `Processed ${data.processed} scheduled post(s)` : "Queue refreshed — nothing due right now");
    load();
  };

  const toggleSchedule = async (s: ScheduleRow) => {
    const { error } = await supabase.from("blog_schedules").update({ active: !s.active }).eq("id", s.id);
    if (error) toast.error(error.message); else { toast.success(s.active ? "Paused" : "Activated"); load(); }
  };

  const deleteSchedule = async (s: ScheduleRow) => {
    if (!confirm(`Delete schedule "${s.name}"? Its pending queue items are removed too.`)) return;
    const { error } = await supabase.from("blog_schedules").delete().eq("id", s.id);
    if (error) toast.error(error.message); else { toast.success("Schedule deleted"); setEditing(null); load(); }
  };

  const cancelQueued = async (r: QueueRow) => {
    const { error } = await supabase.from("blog_scheduled_posts").update({ status: "cancelled" }).eq("id", r.id);
    if (error) toast.error(error.message); else { toast.success("Cancelled"); load(); }
  };

  const restoreQueued = async (r: QueueRow) => {
    const { error } = await supabase.from("blog_scheduled_posts")
      .update({ status: "scheduled", error: null, processed_at: null, attempts: 0 })
      .eq("id", r.id);
    if (error) toast.error(error.message); else { toast.success("Moved back to upcoming"); load(); }
  };

  const deleteQueued = async (r: QueueRow) => {
    if (!confirm(`Delete "${r.topic}" from the queue? This cannot be undone.`)) return;
    const { error } = await supabase.from("blog_scheduled_posts").delete().eq("id", r.id);
    if (error) toast.error(error.message); else { toast.success("Removed from queue"); load(); }
  };

  const editQueued = async (r: QueueRow, values: QueueEdit) => {
    const { error } = await supabase.from("blog_scheduled_posts").update(values).eq("id", r.id);
    if (error) toast.error(error.message); else { toast.success("Scheduled post updated"); load(); }
  };

  const togglePublish = async (p: Post) => {
    const status = p.status === "published" ? "draft" : "published";
    const { error } = await supabase.from("blog_posts")
      .update({ status, published_at: status === "published" ? new Date().toISOString() : null })
      .eq("id", p.id);
    if (error) toast.error(error.message); else { toast.success(status === "published" ? "Published" : "Unpublished"); load(); }
  };

  const remove = async (p: Post) => {
    if (!confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", p.id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); load(); }
  };

  const signOut = async () => { await supabase.auth.signOut(); navigate("/admin/login"); };

  if (loading) return <Layout><div className="container-page py-16">Loading…</div></Layout>;
  if (user && !isAdmin) return (
    <Layout>
      <Seo title="Admin Blog" description="Manage blog content." />
      <div className="container-page max-w-xl py-16">
        <h1 className="font-display mb-3 text-2xl font-bold">No admin access</h1>
        <p className="mb-4 text-muted-foreground">Your account is authenticated but does not have the admin role.</p>
        <Button variant="outline" onClick={signOut}>Sign out</Button>
      </div>
    </Layout>
  );

  const upcoming = queue.filter((q) => ["scheduled", "processing"].includes(q.status));
  const history = queue.filter((q) => !["scheduled", "processing"].includes(q.status))
    .sort((a, b) => (a.scheduled_at < b.scheduled_at ? 1 : -1));

  return (
    <Layout>
      <Seo title="Admin · Blog" description="Manage blog posts." />
      <section className="container-page py-12">
        <div className="mb-10 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Blog Admin</h1>
            <p className="text-sm text-muted-foreground">Generate posts on demand or put your content calendar on autopilot.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={runNow} disabled={running}>
              <RefreshCw className={`mr-2 h-4 w-4 ${running ? "animate-spin" : ""}`} />{running ? "Running…" : "Run now"}
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}><LogOut className="mr-2 h-4 w-4" />Sign out</Button>
          </div>
        </div>

        <Section title="1. Create Post" subtitle="Generate a draft immediately, or queue a single post for a specific date and time.">
          <CreatePostCard userId={user?.id} onQueued={load} />
        </Section>

        <Section title="2. Create Schedule" subtitle="Recurring automatic content. Runs on the server — your browser can stay closed.">
          <div ref={scheduleFormRef}>
            <CreateScheduleCard editing={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          </div>
          <div className="mt-6">
            <SchedulesList rows={schedules} onEdit={startEdit} onToggle={toggleSchedule} onDelete={deleteSchedule} />
          </div>
        </Section>

        <Section title="3. Upcoming Posts" subtitle="Everything the server will generate next.">
          <QueueList rows={upcoming} emptyText="Nothing queued yet." onCancel={cancelQueued} onEdit={editQueued} onDelete={deleteQueued} />
        </Section>

        <Section title="4. Schedule History" subtitle="Runs already processed by the server.">
          <QueueList
            rows={history.slice(0, 25)}
            emptyText="No runs yet."
            onEdit={editQueued}
            onRestore={restoreQueued}
            onDelete={deleteQueued}
          />
        </Section>

        <Section title={`All posts (${posts.length})`}>
          <div className="space-y-2">
            {posts.map((p) => (
              <div key={p.id} className="flex flex-col justify-between gap-3 rounded-lg border border-border bg-card p-4 md:flex-row md:items-center">
                <div className="min-w-0">
                  <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                    <span className={`rounded px-2 py-0.5 ${p.status === "published" ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"}`}>{p.status}</span>
                    <span className="text-muted-foreground">{p.category}</span>
                  </div>
                  <div className="truncate font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">/{p.slug}</div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Link to={`/admin/blog/${p.id}`}><Button variant="outline" size="sm">Edit</Button></Link>
                  <Button variant="outline" size="sm" onClick={() => togglePublish(p)}>{p.status === "published" ? "Unpublish" : "Publish"}</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(p)}>Delete</Button>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="text-sm text-muted-foreground">No posts yet.</p>}
          </div>
        </Section>
      </section>
    </Layout>
  );
}
